import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { DataUpdateRequestService } from 'src/app/dashboard/services/data-update-request.service';

/**
 * Guard del bloqueo determinante por solicitud de actualización pendiente.
 *
 * Patrón: `CanActivateChild` aplicado al árbol `/wallet/client/*` en
 * `dashboard-routing.module.ts`. Lazy-loads el status al primer match y
 * cachea (`ensureLoaded`).
 *
 * Reglas:
 *  - Si `bloqueado === false` → pasa.
 *  - Si `bloqueado === true` y la ruta destino es la pantalla directa
 *    del formulario (`/wallet/client/data-update-request/:id`) → pasa
 *    (acceso por deep link válido).
 *  - Si `bloqueado === true` y la ruta destino es otra del árbol cliente
 *    → redirige a `/wallet/home`. El `UserHomeComponent` detecta el
 *    estado vía `status$` y embebe `<app-data-update-request>` con el
 *    contexto amigable que define la UX final.
 */
@Injectable({ providedIn: 'root' })
export class DataUpdateBlockGuard implements CanActivateChild {
  /** Ruta destino cuando el cliente queda bloqueado. */
  private static readonly HOME_URL = '/wallet/home';
  /** Ruta directa del formulario (acceso opcional vía deep link). */
  private static readonly FORM_BASE_URL = '/wallet/client/data-update-request';

  constructor(
    private service: DataUpdateRequestService,
    private router: Router,
  ) {}

  async canActivateChild(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    const status = await this.service.ensureLoaded();

    if (!status.bloqueado) {
      return true;
    }

    // Deep link directo a la pantalla del formulario sigue siendo válido
    // por compatibilidad (notificaciones por email, links externos).
    if (state.url.startsWith(DataUpdateBlockGuard.FORM_BASE_URL)) {
      return true;
    }

    // Redirige al home — `UserHomeComponent` muestra el form embebido.
    return this.router.createUrlTree([DataUpdateBlockGuard.HOME_URL]);
  }
}
